/**
 * Array utility functions for defensive programming
 * Prevents crashes when API responses return non-array values
 *
 * Handles both response formats:
 * - Raw array: [...]
 * - Wrapped response: { success: true, data: [...], metadata: {...} }
 */

/**
 * Safely converts any value to an array
 * @param {any} value - Value to convert to array
 * @returns {Array} - Always returns an array
 */
export const safeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  // Handle {success: true, data: [...]} response format
  if (value && typeof value === 'object' && Array.isArray(value.data)) return value.data;
  return [];
};

/**
 * Safely extracts data from API response (works for both array and object responses)
 * @param {any} value - API response value
 * @returns {any} - Extracted data or original value
 */
export const safeData = (value) => {
  if (value === null || value === undefined) return null;
  // Handle {success: true, data: {...}} response format
  if (value && typeof value === 'object' && 'success' in value && 'data' in value) {
    return value.data;
  }
  return value;
};

/**
 * Safely get array from object property
 * @param {object} obj - Object to get property from
 * @param {string} key - Property key
 * @returns {Array} - Always returns an array
 */
export const safeArrayProp = (obj, key) => {
  if (!obj || typeof obj !== 'object') return [];
  const value = obj[key];
  return safeArray(value);
};

export default safeArray;
